import logstash from "logstash";
import moment from "moment";
import { toast } from "react-toastify";

import { types, getEnv, getSnapshot, flow } from "mobx-state-tree";

import Identity from "./models/identity";
import Menus from "./models/menus";
import Screens from "./models/screens";
import Customers from "./models/customers";
import Stats from "./models/stats";
import Errors from "./models/errors";
import Products from "./models/products";
import Sessions from "./models/sessions";
import Checkouts from "./models/checkouts";
import ScreenEvents from "./models/screenEvents";
import Notifications from "./models/notifications";

import createConnector from "../lib/connector";
import mapToList from "../lib/mapToList";
import obfuscate from "../lib/obfuscate";

let store = null;
let actionCancellationMap = new Map();
const useOfflineMode = process.env.REACT_APP_OFFLINE_MODE === "true";

// Set start of week to Monday
moment.updateLocale("en", {
  week: {
    dow: 1,
  },
});

const connector = createConnector({
  auth: {
    scopes: process.env.REACT_APP_IDENTITY_SCOPES,
    clientId: process.env.REACT_APP_IDENTITY_CLIENT_ID,
    clientSecret: process.env.REACT_APP_IDENTITY_CLIENT_SECRET,
    tokenUrl: process.env.REACT_APP_IDENTITY_TOKEN_URL,
  },
  customerApiUrl: process.env.REACT_APP_CUSTOMER_API_URL,
  importApiUrl: process.env.REACT_APP_IMPORT_API_URL,
  portalApiUrl: process.env.REACT_APP_PORTAL_API_URL,
  insightsApiUrl: process.env.REACT_APP_INSIGHTS_API_URL,
  moneyApiUrl: process.env.REACT_APP_MONEY_API_URL,
  mailApiUrl: process.env.REACT_APP_MAIL_API_URL,
});

const logger = useOfflineMode
  ? console
  : logstash(process.env.REACT_APP_LOGSTASH_URL, ["portal", "vendo"]);

const PortalStore = types
  .model("PortalStore", {
    isDemoPortal: false,
    showDemoScreens: false,
    activeDialog: types.maybeNull(types.string),
    identity: types.optional(Identity, {}),
    customers: types.optional(Customers, {}),
    checkouts: types.optional(Checkouts, {}),
    screens: types.optional(Screens, {}),
    sessions: types.optional(Sessions, {}),
    products: types.optional(Products, {}),
    menus: types.optional(Menus, {}),
    screenEvents: types.optional(ScreenEvents, {}),
    errors: types.optional(Errors, {}),
    stats: types.optional(Stats, {}),
    notifications: types.optional(Notifications, {}),
    toDate: types.optional(types.string, new Date().toISOString()),
    fromDate: types.optional(
      types.string,
      moment()
        .subtract(1, "month")
        .toISOString()
    ),
    pendingActions: types.optional(types.map(types.boolean), {}),
  })
  .views(self => ({
    get customerId() {
      if (!self.identity.user) {
        return null;
      }

      return self.identity.user.customerId;
    },
    get localeOptions() {
      // TODO: Get available locales directly from API
      const options = self.products.items.length
        ? self.products.items[0].localeOptions
        : [];
      return options.map(({ key, label }) => ({ label, value: key }));
    },
    get currencyOptions() {
      // TODO: Get available currencies directly from API
      const options = self.products.items.length
        ? self.products.items[0].currencyOptions
        : [];
      return options.map(({ key, label }) => ({ label, value: key }));
    },
    get pendingActionCount() {
      const actionList = mapToList(self.pendingActions, true);
      return actionList.length;
    },
    get hasPendingActions() {
      return self.pendingActionCount > 0;
    },
  }))
  .actions(self => ({
    reset() {
      for (const [actionName, cancel] of actionCancellationMap) {
        console.debug(`Cancelling pending action [${actionName}]`);
        cancel();
      }

      self.pendingActions = {};

      self.identity.reset();
      self.customers.reset();
      self.screenEvents.reset();
      self.checkouts.reset();
      self.sessions.reset();
      self.screens.reset();
      self.menus.reset();
      self.errors.reset();
      self.stats.reset();
      self.notifications.reset();
    },
    handleError(err, message) {
      self.createToast(`${message} - ${err.message}`, "error");
      const meta = self.user ? getSnapshot(self.user) : {};
      getEnv(self).logger.error(err, meta);
    },
    setActiveDialog(dialog) {
      self.activeDialog = dialog ? dialog : null;
    },
    setShowDemoScreens(value) {
      self.showDemoScreens = value;
    },
    setFromDate(fromDate) {
      self.fromDate = moment(fromDate)
        .startOf("day")
        .toISOString();
    },
    setToDate(toDate) {
      self.toDate = moment(toDate)
        .endOf("day")
        .toISOString();
    },
    createToast(message, level = "default") {
      const type = toast.TYPE[level.toUpperCase()];

      toast(message, { type });
    },

    /**
     * Runs an action
     * actionName {string}
     * action {function}
     * cancel {function}
     */
    runAction: flow(function* runAction(
      actionName,
      action,
      cancel,
      throwExceptions = false
    ) {
      const existingAction = self.pendingActions.get(actionName);
      const cancelExistingAction = actionCancellationMap.get(actionName);

      if (cancelExistingAction) {
        cancelExistingAction();
      } else if (existingAction) {
        return;
      }

      self.pendingActions.set(actionName, true);
      actionCancellationMap.set(actionName, cancel);

      try {
        yield action();
      } catch (err) {
        if (err.message === getEnv(self).connector.REQUEST_CANCELLED) {
          return;
        }

        if (err.message === getEnv(self).connector.ACCESS_TOKEN_EXPIRED) {
          console.debug(`Access token expired [${actionName}]`);
          return;
        }

        if (throwExceptions) {
          throw err;
        }

        self.handleError(err, `Could not run action ${actionName}`);
      } finally {
        self.pendingActions.delete(actionName);
        actionCancellationMap.delete(actionName);
      }
    }),
  }));

export default function ensureStore() {
  if (store !== null) {
    return store;
  }

  store = PortalStore.create(
    { isDemoPortal: process.env.REACT_APP_IS_DEMO_PORTAL === "true" },
    { connector, logger, obfuscate }
  );

  connector.auth.events.on("onSignedIn", store.identity.setUser);
  connector.auth.events.on("onSignedOut", store.reset);
  connector.auth.tryRestoreAccessToken();

  return store;
}
